import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { AppContext } from "../../../../appContext";
import { DashboardContext } from "../../DashboardContext";
import { getSessions, getSessionsWithResults } from "../../../../api/sessionApi";
import { getClients } from "../../../../api/api";
import { serverToClientSession } from "../../../../api/sessionApi";
import { Spinner } from "../../../../components/spinner/Spinner";
import { useNavigate } from "react-router-dom";
import { VideoIcon } from "../../../../components/icons/VideoIcon";
import { parseTimezoneAwareDatetime, formatDateOnly, formatTimeOnly } from "../../../../utils/timezone";
import "./ScheduleTab.css";

interface ISessionDisplay {
    clientId: string;
    clientName: string;
    sessionId: string;
    sessionDate: string;
    sessionTitle: string;
    hasAnalysis: boolean;
    avatarUrl?: string;
    customAvatarUrl?: string;
}

// Global reference to trigger schedule reload
let scheduleReloadTrigger: (() => void) | null = null;

// Utility function to clear schedule cache - can be called from other components
export const clearScheduleCache = () => {
    localStorage.removeItem('cached_all_sessions');
    localStorage.removeItem('cached_all_sessions_timestamp');
    // Trigger reload if component is mounted
    if (scheduleReloadTrigger) {
        scheduleReloadTrigger();
    }
};

export const ScheduleTab = () => {
    const [allSessions, setAllSessions] = useState<ISessionDisplay[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastDate, setLastDate] = useState<string>('');
    const [lastId, setLastId] = useState<string>('');
    const [hasMore, setHasMore] = useState(true);
    const [cacheVersion, setCacheVersion] = useState(0);
    const { translate, clients, sessionsRaw, currentLanguage } = useContext(AppContext);
    const { searchValue } = useContext(DashboardContext);
    const navigate = useNavigate();

    // Add error boundary effect
    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            console.error('ScheduleTab: Global error caught:', event.error);
            console.error('ScheduleTab: Error details:', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack
            });
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            console.error('ScheduleTab: Unhandled promise rejection:', event.reason);
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    useEffect(() => {
        // Register reload trigger
        scheduleReloadTrigger = () => {
            setCacheVersion(prev => prev + 1);
        };

        // Cleanup on unmount
        return () => {
            scheduleReloadTrigger = null;
        };
    }, []);

    useEffect(() => {
        console.log('ScheduleTab: useEffect triggered, cacheVersion:', cacheVersion);
        console.log('ScheduleTab: clients state:', clients ? clients.length : 'null/undefined');
        
        try {
            // Try to load from cache
            const cachedSessions = localStorage.getItem('cached_all_sessions');
            const cacheTimestamp = localStorage.getItem('cached_all_sessions_timestamp');
            const now = Date.now();

            console.log('ScheduleTab: Cache check:', {
                hasCachedSessions: !!cachedSessions,
                hasCacheTimestamp: !!cacheTimestamp,
                cacheAge: cacheTimestamp ? now - parseInt(cacheTimestamp) : 'N/A'
            });

            // If cache is fresh (less than 5 minutes), use it
            if (cachedSessions && cacheTimestamp && (now - parseInt(cacheTimestamp)) < 5 * 60 * 1000) {
                try {
                    const parsed = JSON.parse(cachedSessions);
                    console.log('ScheduleTab: Using cached data:', parsed);
                    // Check if cache has the new format with pagination state
                    if (parsed.sessions && parsed.lastDate !== undefined && parsed.lastId !== undefined && parsed.hasMore !== undefined) {
                        setAllSessions(parsed.sessions);
                        setLastDate(parsed.lastDate);
                        setLastId(parsed.lastId);
                        setHasMore(parsed.hasMore);
                    } else {
                        // Old cache format, just use sessions
                        setAllSessions(parsed);
                    }
                    setLoading(false);
                    return;
                } catch (e) {
                    console.error('ScheduleTab: Cache parsing error:', e);
                    // If parsing error, load fresh data
                }
            }

            console.log('ScheduleTab: Loading fresh sessions data');
            loadAllSessions();
        } catch (error) {
            console.error('ScheduleTab: Error in useEffect:', error);
            setLoading(false);
        }
    }, [cacheVersion]); // Reload when cache version changes

    const loadAllSessions = useCallback(async (isLoadMore = false) => {
        console.log('ScheduleTab: loadAllSessions called, isLoadMore:', isLoadMore);
        console.log('ScheduleTab: Current state:', {
            clients: clients ? clients.length : 'null/undefined',
            lastDate,
            lastId,
            hasMore
        });

        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setAllSessions([]);
                setLastDate('');
                setLastId('');
                setHasMore(true);
            }

            console.log('ScheduleTab: Making API call to getSessionsWithResults');
            
            // Get sessions with pagination
            const sessionsResponse = await getSessionsWithResults(
                undefined, 
                10, // limit to 10 sessions
                isLoadMore ? lastDate : undefined,
                isLoadMore ? lastId : undefined
            );

            console.log('ScheduleTab: API response status:', sessionsResponse.status);

            if (!sessionsResponse.ok) {
                throw new Error(`Failed to load sessions: ${sessionsResponse.status} ${sessionsResponse.statusText}`);
            }

            const rawSessions = await sessionsResponse.json();
            console.log('ScheduleTab: Raw sessions data:', rawSessions);

            // Convert sessions to the needed format
            console.log('ScheduleTab: Converting sessions, count:', rawSessions.length);
            const allSessionsData = rawSessions.map((sessionWrapper: any, index: number) => {
                try {
                    const sessionId = Object.keys(sessionWrapper)[0];
                    const sessionData = sessionWrapper[sessionId];
                    const converted = serverToClientSession({
                        ...sessionData,
                        id: sessionId
                    });
                    console.log(`ScheduleTab: Converted session ${index}:`, converted);
                    return converted;
                } catch (error) {
                    console.error(`ScheduleTab: Error converting session ${index}:`, error, sessionWrapper);
                    throw error;
                }
            });

            // Use already loaded clients from context
            const clientsData = clients || [];
            console.log('ScheduleTab: Clients data:', clientsData);
            
            if (!Array.isArray(clientsData)) {
                throw new Error('Clients data is not an array: ' + typeof clientsData);
            }

            const clientsMap = clientsData.reduce((acc: any, client: any, index: number) => {
                try {
                    if (!client || !client.id) {
                        console.warn(`ScheduleTab: Invalid client at index ${index}:`, client);
                        return acc;
                    }
                    acc[client.id] = {
                        id: client.id,
                        Name: client.firstName,
                        Soname: client.lastName,
                        avatarUrl: client.avatarUrl,
                        customAvatarUrl: client.customAvatarUrl
                    };
                    return acc;
                } catch (error) {
                    console.error(`ScheduleTab: Error processing client ${index}:`, error, client);
                    return acc;
                }
            }, {});
            
            console.log('ScheduleTab: Clients map created:', Object.keys(clientsMap));

            // Filter only completed sessions (past dates)
            const now = new Date();
            console.log('ScheduleTab: Filtering sessions, total count:', allSessionsData.length);
            const completedSessions = allSessionsData.filter((session: any, index: number) => {
                try {
                    const sessionDatetime = session.startDatetime || session.date;
                    if (!sessionDatetime) {
                        console.warn(`ScheduleTab: Session ${index} has no date:`, session);
                        return false;
                    }
                    const sessionDate = session.startDatetime ? parseTimezoneAwareDatetime(session.startDatetime) : new Date(session.date || 0);
                    const isCompleted = sessionDate < now;
                    console.log(`ScheduleTab: Session ${index} date check:`, {
                        date: sessionDatetime,
                        sessionDate: sessionDate.toISOString(),
                        now: now.toISOString(),
                        isCompleted
                    });
                    return isCompleted;
                } catch (error) {
                    console.error(`ScheduleTab: Error filtering session ${index}:`, error, session);
                    return false;
                }
            });

            console.log('ScheduleTab: Completed sessions count:', completedSessions.length);

            // Group sessions by client
            const clientSessionsMap = new Map<string, any[]>();

            completedSessions.forEach((session: any, index: number) => {
                try {
                    const clientId = session.client;
                    if (!clientId) {
                        console.warn(`ScheduleTab: Session ${index} has no client ID:`, session);
                        return;
                    }
                    if (!clientSessionsMap.has(clientId)) {
                        clientSessionsMap.set(clientId, []);
                    }
                    clientSessionsMap.get(clientId)?.push(session);
                } catch (error) {
                    console.error(`ScheduleTab: Error grouping session ${index}:`, error, session);
                }
            });

            console.log('ScheduleTab: Grouped by clients:', Array.from(clientSessionsMap.keys()));

            // Sort sessions within each client by date (newest first)
            clientSessionsMap.forEach((sessions, clientId) => {
                sessions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            });

            // Implement round-robin logic
            console.log('ScheduleTab: Starting round-robin logic');
            const result: ISessionDisplay[] = [];
            const clientIds = Array.from(clientSessionsMap.keys());
            const sessionCounts = Array.from(clientSessionsMap.values()).map(sessions => sessions.length);
            const maxSessions = sessionCounts.length > 0 ? Math.max(...sessionCounts) : 0;

            console.log('ScheduleTab: Round-robin parameters:', {
                clientIds,
                sessionCounts,
                maxSessions
            });

            for (let sessionIndex = 0; sessionIndex < maxSessions; sessionIndex++) {
                for (const clientId of clientIds) {
                    try {
                        const clientSessions = clientSessionsMap.get(clientId) || [];
                        if (sessionIndex < clientSessions.length) {
                            const session = clientSessions[sessionIndex];
                            const client = clientsMap[clientId];

                            console.log(`ScheduleTab: Processing session ${sessionIndex} for client ${clientId}:`, {
                                session: session,
                                client: client
                            });

                            if (client) {
                                const displaySession = {
                                    clientId: clientId,
                                    clientName: `${client.Name || ''} ${client.Soname || ''}`.trim(),
                                    sessionId: session.id,
                                    sessionDate: session.startDatetime || session.date,
                                    sessionTitle: session.title || 'Session',
                                    hasAnalysis: session.PredictionID ? true : false,
                                    avatarUrl: client.avatarUrl,
                                    customAvatarUrl: client.customAvatarUrl
                                };
                                result.push(displaySession);
                                console.log('ScheduleTab: Added session to result:', displaySession);
                            } else {
                                console.warn(`ScheduleTab: Client not found for ID ${clientId}`);
                            }
                        }
                    } catch (error) {
                        console.error(`ScheduleTab: Error in round-robin for session ${sessionIndex}, client ${clientId}:`, error);
                    }
                }
            }

            console.log('ScheduleTab: Final result count:', result.length);

            // Update pagination state
            console.log('ScheduleTab: Updating pagination state');
            if (result.length > 0) {
                const lastSession = result[result.length - 1];
                console.log('ScheduleTab: Setting pagination from last session:', lastSession);
                setLastDate(lastSession.sessionDate);
                setLastId(lastSession.sessionId);
            }

            // Check if we have more data
            const hasMoreData = result.length === 10;
            console.log('ScheduleTab: Setting hasMore to:', hasMoreData);
            setHasMore(hasMoreData);

            // Update sessions list
            if (isLoadMore) {
                console.log('ScheduleTab: Appending to existing sessions');
                setAllSessions(prev => {
                    const updated = [...prev, ...result];
                    console.log('ScheduleTab: Updated sessions count:', updated.length);
                    return updated;
                });
            } else {
                console.log('ScheduleTab: Setting new sessions');
                setAllSessions(result);
                // Save to cache only for initial load with pagination state
                const cacheData = {
                    sessions: result,
                    lastDate: result.length > 0 ? result[result.length - 1].sessionDate : '',
                    lastId: result.length > 0 ? result[result.length - 1].sessionId : '',
                    hasMore: result.length === 10
                };
                console.log('ScheduleTab: Saving to cache:', cacheData);
                try {
                    localStorage.setItem('cached_all_sessions', JSON.stringify(cacheData));
                    localStorage.setItem('cached_all_sessions_timestamp', Date.now().toString());
                } catch (cacheError) {
                    console.error('ScheduleTab: Error saving to cache:', cacheError);
                }
            }

            console.log('ScheduleTab: loadAllSessions completed successfully');
        } catch (error) {
            console.error('ScheduleTab: Failed to load sessions:', error);
            console.error('ScheduleTab: Error stack:', error instanceof Error ? error.stack : 'No stack available');
            
            // Set error state
            setAllSessions([]);
            setHasMore(false);
        } finally {
            console.log('ScheduleTab: Setting loading states to false');
            setLoading(false);
            setLoadingMore(false);
        }
    }, [clients, lastDate, lastId]);

    const handleWatchSession = (clientId: string, sessionId: string) => {
        navigate(`/meeting/${clientId}/${sessionId}`);
    };

    // Filter sessions by search query
    const filteredSessions = useMemo(() => {
        if (!searchValue) {
            return allSessions;
        }
        return allSessions.filter(session => 
            session.clientName.toLowerCase().includes(searchValue.toLowerCase())
        );
    }, [allSessions, searchValue]);

     const handleScroll = useCallback((event: any) => {
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 20 && hasMore && !loadingMore) {
            loadAllSessions(true);
        }
    }, [loadingMore, hasMore, loadAllSessions]);

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className="scheduleTab">
            <div className="scheduleTab_list" onScroll={handleScroll}>
                {filteredSessions.length === 0 ? (
                    <div className="scheduleTab_empty">
                        {allSessions.length === 0 ? translate('schedule_no_sessions_found') : translate('schedule_no_sessions_match_search')}
                    </div>
                ) : (
                    <>
                        {filteredSessions.map((session, index) => {
                            try {
                                // Format compact date and time for single line display
                                const getFormattedDateTime = () => {
                                    if (!session.sessionDate) return '';
                                    
                                    const date = new Date(session.sessionDate);
                                    if (isNaN(date.getTime())) return '';
                                    
                                    const dayShortKeys = ['day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat'];
                                    const monthShortKeys = ['month_jan', 'month_feb', 'month_mar', 'month_apr', 'month_may_short', 'month_jun', 
                                                          'month_jul', 'month_aug', 'month_sep', 'month_oct', 'month_nov', 'month_dec'];
                                    
                                    const dayIndex = date.getDay();
                                    const monthIndex = date.getMonth();
                                    const dayNumber = date.getDate();
                                    
                                    const dayShort = translate(dayShortKeys[dayIndex]);
                                    const monthShort = translate(monthShortKeys[monthIndex]);
                                    const timeStr = formatTimeOnly(session.sessionDate, undefined, currentLanguage);
                                    
                                    // Single line: Day, DD Mon, HH:MM
                                    return `${dayShort}, ${dayNumber} ${monthShort}, ${timeStr}`;
                                };
                                
                                const dateTimeStr = getFormattedDateTime();
                                
                                return (
                                    <div 
                                        key={`${session.clientId}-${session.sessionId}-${index}`} 
                                        className={`scheduleTab_item ${session.hasAnalysis ? 'scheduleTab_item--clickable' : ''}`}
                                        onClick={session.hasAnalysis ? () => handleWatchSession(session.clientId, session.sessionId) : undefined}
                                    >
                                        <div 
                                            className="scheduleTab_item_avatar" 
                                            style={{backgroundImage: (session.customAvatarUrl || session.avatarUrl) ? `url(${session.customAvatarUrl || session.avatarUrl})` : undefined}}
                                        />
                                        <div className="scheduleTab_item_info">
                                            <div className="scheduleTab_item_mainInfo">
                                                <span className="scheduleTab_item_clientName">
                                                    {session.clientName}
                                                </span>
                                            </div>
                                            <span className="scheduleTab_item_sessionDate">
                                                {dateTimeStr}
                                            </span>
                                        </div>
                                        <div className="scheduleTab_item_actions">
                                            {session.hasAnalysis ? (
                                                <div className="scheduleTab_watchButton">
                                                    <VideoIcon size={18} color="#14AE5C" />
                                                </div>
                                            ) : (
                                                <div className="scheduleTab_noAnalysis">
                                                    {translate('session_processing')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            } catch (error) {
                                console.error('Error rendering schedule item:', error, session);
                                return null;
                            }
                        })}
                        {loadingMore && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                <Spinner />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};